/**
 * @name ProgressBar
 * @description Componente de barra de progreso
 * @author Ricardo Guerra
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CountUp from 'react-countup';

import { Flex, Theme, Typography } from '@styles';

// Material-UI
import MuiLinearProgress from '@material-ui/core/LinearProgress';

const ProgressBar = (props) => {
  const { color, label, showValue, value } = props;

  const [completed, setCompleted] = useState(0);
  const completedRef = useRef(0);

  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    let animationFrameId;
    const startValue = completedRef.current;
    const endValue = value;
    const duration = 520;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - ((1 - progress) ** 3);
      const nextValue = startValue + ((endValue - startValue) * easedProgress);

      setCompleted(nextValue);

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(animate);
      }
    };

    animationFrameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [value]);

  return (
    <Flex.Col flex="100%">
      <Flex.Row alignItems="baseline">
        {label && (
          <Typography.Medium>{label}</Typography.Medium>
        )}
        {showValue && (
          <Styled.Value>
            <CountUp className="countup-value" end={value} decimals={value % 1 === 0 ? 0 : 2} delay={1} style={{ color: Theme.colors.greyDark }} />
            <Typography.H6 color="greyDarkAlpha" style={{ fontFamily: 'Bryant Pro Regular' }}>{'%'}</Typography.H6>
          </Styled.Value>
        )}
      </Flex.Row>
      <Styled.BoxRow>
        <Styled.LinearProgress variant="determinate" colorBar={color} value={completed} />
      </Styled.BoxRow>
    </Flex.Col>
  );
};

ProgressBar.propTypes = {
  // Color de la barra de progreso.
  color: PropTypes.string,
  // Etiqueta de la barra de progreso.
  label: PropTypes.string,
  // Boolean que indica si se muestra el valor de la barra de progreso.
  showValue: PropTypes.bool,
  // Valor de la barra de progreso.
  value: PropTypes.number.isRequired,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxRow = styled.div`
  width: 100%;
`;

Styled.Value = styled.div`
  display: flex;
  flex: 1;
  align-items: baseline;
  justify-content: flex-end;

  .countup-value {
    font-family: 'Bryant Pro Medium';
    font-variant: tabular-nums;
    font-size: ${(props) => props.theme.typography.heading6};
    min-width: 48px;
    text-align: right;
  }
`;

Styled.LinearProgress = styled(MuiLinearProgress)`
  height: 6px;
  border-radius: 6px;

  &.MuiLinearProgress-colorPrimary {
    background-color:  ${(props) => props.theme.colors.greyLight};
  }

  .MuiLinearProgress-barColorPrimary {
    background-color: ${(props) => props.colorBar ? props.theme.colors[props.colorBar] : props.theme.colors['blueMedium']};
  }
`;

export default ProgressBar;
